import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import GridFilterPanel from '../../commons/filters/GridFilterPanel.jsx';
import ConfigBusiness from '../../commons/config/ConfigBusiness.js';
import * as Utils from '../../commons/utils/Utils';
import {
	MOVILES_FILTROS_STORAGE_KEY,
	buildMovilesFilterFields,
	getMovilesFilterDefaults,
} from './movilesFilterFields.js';
import { CLAVE_ETIQUETA, aOpciones, aOpcionesDeConfig } from './movilesFilterOptions.js';

/**
 * Filtros de la grilla de Móviles.
 *
 * VEC-3690: este componente pasó de tener el panel escrito a mano (1368 líneas, 33 inputs,
 * su propio estado y su propia persistencia) a ser solo la capa que ALIMENTA a
 * <GridFilterPanel />: carga las opciones de los desplegables y las normaliza a
 * `{value, label}`. El estado, la persistencia y la serialización del query son del panel
 * compartido; ver docs/design-system/grid-filter-panel.md.
 *
 * Los nombres de campo y los tipos de control se mantuvieron iguales, así el query que llega
 * al backend no cambió. Pasar campos de simple a múltiple es la fase 2.
 */

const OPTIONS_INIT = {
	marca: [],
	estado: [],
	tipo: [],
	pais: [],
	region: [],
	subRegion: [],
	base: [],
	titular: [],
	companiaOrigen: [],
	centroCosto: [],
	dominio: [],
	responsables2: [],
	supervisores: [],
	temporales: [],
	rutas: [],
	transportadora: [],
	rentadora: [],
	funcion: [],
	esReserva: [],
	estadoVinculacion: [],
};

const MovilesGridFiltros = (props) => {
	const intl = useIntl();
	const [options, setOptions] = useState(OPTIONS_INIT);
	const [optionsLoading, setOptionsLoading] = useState(true);

	const fields = buildMovilesFilterFields(props.columnsEnabled);

	const esReservaOpciones = [
		{ value: true, label: intl.formatMessage({ id: 'Si', defaultMessage: 'Si' }) },
		{ value: false, label: intl.formatMessage({ id: 'No', defaultMessage: 'No' }) },
	];

	const estadoVinculacionOpciones = [
		{ value: 'con_relacion', label: intl.formatMessage({ id: 'MovilesGridFiltros.render.filtros_estadoVinculacion.con_relacion', defaultMessage: 'Con relación activa' }) },
		{ value: 'es_principal', label: intl.formatMessage({ id: 'MovilesGridFiltros.render.filtros_estadoVinculacion.es_principal', defaultMessage: 'Es principal' }) },
		{ value: 'es_asociado', label: intl.formatMessage({ id: 'MovilesGridFiltros.render.filtros_estadoVinculacion.es_asociado', defaultMessage: 'Es asociado' }) },
		{ value: 'sin_relacion', label: intl.formatMessage({ id: 'MovilesGridFiltros.render.filtros_estadoVinculacion.sin_relacion', defaultMessage: 'Sin relación' }) },
		{ value: 'asociables', label: intl.formatMessage({ id: 'MovilesGridFiltros.render.filtros_estadoVinculacion.asociables', defaultMessage: 'Asociables' }) },
		{ value: 'con_historial', label: intl.formatMessage({ id: 'MovilesGridFiltros.render.filtros_estadoVinculacion.con_historial', defaultMessage: 'Con historial' }) },
	];

	/**
	 * Estado impuesto desde la grilla: el clasificador de estados del encabezado elige uno o
	 * varios estados y el panel tiene que reflejarlos. Llega como texto separado por comas.
	 *
	 * Las opciones de `estado` salen de config, no de un endpoint, así que se arman igual que
	 * en `aOpcionesDeConfig` en vez de buscarlas en `options`: si el valor impuesto no
	 * estuviera en la lista, igual queremos mostrarlo.
	 */
	const estadoImpuesto = () => {
		if (!props.overrideEstados) return null;
		return { estado: aOpcionesDeConfig(props.overrideEstados) };
	};

	useEffect(() => {
		setOptionsLoading(true);

		// *** CADA LISTA SE CARGA CON EL MISMO FLAG QUE MUESTRA SU CAMPO. ***
		// Son flags distintos y es fácil cruzarlos: el campo Rentadora se gatea con
		// `frontend.rentadora.grilla` (el `rentingEnabled` de configColumns), NO con
		// `moviles.rentadora.habilitado`. Al migrar el panel usé el segundo, que estaba en false, y
		// el filtro se mostraba SIN NINGUNA OPCIÓN: no da error, simplemente no filtra nada.
		const conTransportadoras = ConfigBusiness.get('moviles.transportadora.grilla') === 'true';
		const conRentadoras = ConfigBusiness.get('frontend.rentadora.grilla') === 'true';

		Promise.all([
			Utils.getData('/marcas/select'),
			Utils.getData('/modelo-tipos/select'),
			Utils.getData('/paises/select'),
			Utils.getData('/regiones/select'),
			Utils.getData('/subregiones/select-all'),
			Utils.getData('/bases/select-filtered-by-user/movil'),
			Utils.getData('/movil-titulares/select'),
			Utils.getData('/centros-costos/select'),
			Utils.getData('/rutas/select'),
			Utils.getData('/funciones/select'),
			Utils.getData('/moviles/select-byDominio'),
			// Una sola lista de personas alimenta los tres filtros de gente.
			Utils.getData('/personas/simple-search?filterNotInternal=true&search=&total=all'),
			conTransportadoras ? Utils.getData('/transportadoras/select') : Promise.resolve([]),
			conRentadoras ? Utils.getData('/rentadoras/select') : Promise.resolve([]),
		])
			.then((data) => {
				const personas = aOpciones(data[11]);

				setOptions({
					marca: aOpciones(data[0]),
					tipo: aOpciones(data[1]),
					pais: aOpciones(data[2]),
					region: aOpciones(data[3]),
					subRegion: aOpciones(data[4]),
					base: aOpciones(data[5]),
					titular: aOpciones(data[6]),
					centroCosto: aOpciones(data[7]),
					rutas: aOpciones(data[8]),
					funcion: aOpciones(data[9]),
					dominio: aOpciones(data[10]),
					responsables2: personas,
					supervisores: personas,
					temporales: personas,
					// Estas dos NO devuelven `label`: ver movilesFilterOptions.js. Sin la clave
					// correcta el label queda undefined y MUI rompe el render al abrir el filtro.
					transportadora: aOpciones(data[12], CLAVE_ETIQUETA.transportadora),
					rentadora: aOpciones(data[13], CLAVE_ETIQUETA.rentadora),
					// De config-business, no de un endpoint.
					estado: aOpcionesDeConfig(ConfigBusiness.get('moviles.estados')),
					companiaOrigen: aOpcionesDeConfig(ConfigBusiness.get('moviles.companiaOrigen.valores')),
					esReserva: esReservaOpciones,
					estadoVinculacion: estadoVinculacionOpciones,
				});
			})
			.catch((err) => console.log(err))
			.finally(() => setOptionsLoading(false));
	}, []);

	return (
		<GridFilterPanel
			open={props.open}
			fields={fields}
			options={options}
			defaults={getMovilesFilterDefaults()}
			storageKey={MOVILES_FILTROS_STORAGE_KEY}
			loading={props.loading || optionsLoading}
			applyOnMount={true}
			resetSignal={props.resetSignal}
			onResetSignalHandled={props.onResetSignalHandled}
			overrideState={estadoImpuesto()}
			// El clasificador ACOTA lo que ya estaba filtrado; no arranca de cero.
			overrideMerge={true}
			// Se retiene la señal mientras cargan las opciones: si el panel la consumiera antes,
			// avisaría que la procesó y la grilla la limpiaría sin que se haya aplicado nada.
			overrideSignal={optionsLoading ? null : props.overrideEstados}
			onOverrideSignalHandled={props.onOverrideHandled}
			onApply={props.onApply}
		/>
	);
};

export default MovilesGridFiltros;
